import { createToken, CstNode, CstParser, ICstVisitor, Lexer } from 'chevrotain';
import { extractDestAttr, getFuncRule, getParamDef } from './TransformDSLUtils';
import UIUtils from './UIUtils';

interface TransformDSLAST {
  type: string;
  name?: string;
  parameters?: any;
  value?: any;
  __destAttr?: string;
}

export const TYPE_LABELS = {
  function: 'function call',
  attributeRef: 'attribute reference',
  systemAttributeRef: 'metadata reference',
  string: 'string',
  number: 'number',
  boolean: 'boolean',
};

const getCharList = (startChar: string, endChar: string) => {
  const startCharCode = startChar.charCodeAt(0);
  const endCharCode = endChar.charCodeAt(0);
  const chars = [];

  for (let i = startCharCode; i <= endCharCode; i++) {
    chars.push(String.fromCharCode(i));
  }

  return chars;
};

class TransformDSLParser extends CstParser {
  // Cache properties for OR alternatives
  private expressionOr: any;

  constructor() {
    super(allTokens, {
      maxLookahead: 2,
      skipValidations: true,
    });

    this.performSelfAnalysis();
  }

  functionCall = this.RULE('functionCall', () => {
    this.CONSUME(FunctionName);
    this.CONSUME(LParen);
    this.OPTION(() => {
      this.SUBRULE(this.arguments);
    });
    this.CONSUME(RParen);
  });

  arguments = this.RULE('arguments', () => {
    this.AT_LEAST_ONE_SEP({
      SEP: Comma,
      DEF: () => {
        this.SUBRULE(this.expression);
      },
    });
  });

  expression = this.RULE('expression', () => {
    // Cache the OR alternatives
    this.OR(
      this.expressionOr ||
        (this.expressionOr = [
          {
            ALT: () => this.CONSUME(StringLiteral),
          },
          {
            ALT: () => this.CONSUME(NumberLiteral),
          },
          {
            ALT: () => this.CONSUME(BooleanLiteral),
          },
          {
            ALT: () => this.CONSUME(NullLiteral),
          },
          {
            ALT: () => this.CONSUME(AttributeRef),
          },
          {
            ALT: () => this.CONSUME(MetadataRef),
          },
          {
            ALT: () => this.SUBRULE(this.objectLiteral),
          },
          {
            ALT: () => this.SUBRULE(this.arrayLiteral),
          },
          {
            ALT: () => this.SUBRULE(this.functionCall),
          },
        ]),
    );
  });

  objectLiteral = this.RULE('objectLiteral', () => {
    this.CONSUME(LCurly);
    this.AT_LEAST_ONE_SEP({
      SEP: Comma,
      DEF: () => {
        this.SUBRULE(this.keyValuePair);
      },
    });
    this.CONSUME(RCurly);
  });

  arrayLiteral = this.RULE('arrayLiteral', () => {
    this.CONSUME(LSquare);
    this.OPTION(() => {
      this.SUBRULE(this.arguments);
    });
    this.CONSUME(RSquare);
  });

  keyValuePair = this.RULE('keyValuePair', () => {
    this.OR([
      {
        ALT: () => this.CONSUME(Identifier),
      },
      {
        ALT: () => this.CONSUME(StringLiteral),
      },
    ]);
    this.CONSUME(Colon);
    this.SUBRULE(this.expression);
  });
}

class TransformDSLVisitor implements ICstVisitor<any, TransformDSLAST> {
  private transforms: any | null;

  visit(node: CstNode): TransformDSLAST {
    const visitorMethod = this[node.name];
    if (visitorMethod) {
      return visitorMethod.call(this, node.children);
    }
    return null;
  }

  validateVisitor(): void {
    // No validation needed as we're using direct method calls
  }

  functionCall(ctx: any, transforms: any = null, rulesByName: any = null): TransformDSLAST {
    if (transforms) {
      this.transforms = transforms;
    }

    const children = ctx.children;
    const funcName = children.FunctionName[0].image.trim(); // trim because the function name can end with a space
    const args = children.arguments ? this.arguments(children.arguments[0], rulesByName) : [];

    return funcToTxObject(funcName, args, this.transforms, rulesByName);
  }

  arguments(ctx: any, rulesByName: any = null): TransformDSLAST[] {
    return ctx.children.expression.map((arg: any) => this.expression(arg, rulesByName));
  }

  expression(ctx: any, rulesByName: any = null): TransformDSLAST | TransformDSLAST[] {
    const children = ctx.children;

    if (children.StringLiteral) {
      return {
        type: 'string',
        value: unescapeStringToken(children.StringLiteral[0].image),
      };
    }

    if (children.NumberLiteral) {
      return {
        type: 'number',
        value: children.NumberLiteral[0].image,
      };
    }

    if (children.BooleanLiteral) {
      return {
        type: 'boolean',
        value: children.BooleanLiteral[0].image === 'true',
      };
    }

    if (children.NullLiteral) {
      return {
        type: 'null',
        value: null,
      };
    }

    if (children.AttributeRef) {
      return {
        type: 'attributeRef',
        name: unescapeAttributeRefToken(children.AttributeRef[0].image),
      };
    }

    if (children.MetadataRef) {
      return {
        type: 'systemAttributeRef',
        name: unescapeAttributeRefToken(children.MetadataRef[0].image),
      };
    }

    if (children.objectLiteral) {
      return this.objectLiteral(children.objectLiteral[0]);
    }

    if (children.arrayLiteral) {
      return this.arrayLiteral(children.arrayLiteral[0]);
    }

    if (children.functionCall) {
      return this.functionCall(children.functionCall[0], null, rulesByName);
    }

    throw new Error(`Unsupported expression type: ${JSON.stringify(children)}`);
  }

  objectLiteral(ctx: any): TransformDSLAST[] {
    const pairs = ctx.children.keyValuePair.map((pair: any) => this.keyValuePair(pair));

    return [].concat(...pairs);
  }

  arrayLiteral(ctx: any): TransformDSLAST[] {
    const children = ctx.children;
    const args = children.arguments ? this.arguments(children.arguments[0]) : [];

    return args;
  }

  keyValuePair(ctx: any): [TransformDSLAST, TransformDSLAST | TransformDSLAST[]] {
    const children = ctx.children;
    const key = children.Identifier
      ? children.Identifier[0].image.trim()
      : unescapeStringToken(children.StringLiteral[0].image);
    const value = this.expression(ctx.children.expression[0]);

    return [{ type: 'string', value: key }, value];
  }
}

const unescapeStringToken = (token: string) => {
  // remove the outer quotes and the backslashes used for escaping the inner
  // quotes
  const quote = token[0];

  return token.slice(1, -1).replaceAll(`\\${quote}`, quote);
};

const unescapeAttributeRefToken = (token: string) => {
  return token
    .replace(/^\$\$?[\s\n]*\((.*)\)$/, '$1') // remove the outer parentheses
    .replace(/\\([()])/g, '$1'); // remove the backslashes used for escaping the inner parentheses
};

const funcToTxObject = (
  funcName: string,
  args: any[],
  transforms: any | null,
  rulesByName: any | null,
) => {
  const rule = getFuncRule(funcName, rulesByName);

  // validations
  if (!rule) {
    throw new Error(`Unknown function: ${funcName}`);
  }

  const restParams = rule.params?.find(({ inputElement }) => inputElement === 'restParams');

  if (restParams) {
    return {
      type: 'function',
      name: rule.value,
      parameters: args,
    };
  }

  const isCustomTx = !!rule.customTxId;

  if (!isCustomTx) {
    const minExpectedParams = rule.requiredParams?.length || 0;
    const maxExpectedParams = minExpectedParams + (rule.optionalParams?.length || 0);

    if (minExpectedParams === maxExpectedParams && args.length !== minExpectedParams) {
      throw new Error(
        `${funcName} expects ${UIUtils.toPluralFormat('argument', minExpectedParams)} but ${args.length} ${args.length === 1 ? 'was' : 'were'} provided.`,
      );
    }

    if (args.length < minExpectedParams || args.length > maxExpectedParams) {
      throw new Error(
        `${funcName} expects ${minExpectedParams} to ${maxExpectedParams} arguments but ${args.length} ${args.length === 1 ? 'was' : 'were'} provided.`,
      );
    }

    // type-check func args
    args.forEach((arg, index) => {
      const paramDef = getParamDef(rule, index);

      if (!paramDef) {
        return;
      }

      const argType = arg?.type ? arg.type : Array.isArray(arg) ? 'array' : 'undefined';
      const argTypeWithArticle = ['array', 'object'].includes(argType)
        ? `an ${argType}`
        : `a ${argType}`;

      if (
        ['lookup-nosecondary', 'lookup'].includes(paramDef.inputElement) &&
        argType !== 'number'
      ) {
        throw new Error(
          `Argument ${index + 1} of ${rule.label} should be a lookup ID as a number but ${argTypeWithArticle} was provided.`,
        );
      }

      if (paramDef.inputElement === 'array' && argType !== 'array') {
        throw new Error(
          `Argument ${index + 1} of ${rule.label} should be an array literal but ${argTypeWithArticle} was provided.`,
        );
      }

      if (paramDef.inputElement === 'object' && (argType !== 'array' || arg.length % 2 !== 0)) {
        throw new Error(
          `Argument ${index + 1} of ${rule.label} should be an object literal but ${argTypeWithArticle} was provided.`,
        );
      }

      const typesAllowed = paramDef.typesAllowed || [
        'attributeRef',
        'systemAttributeRef',
        'function',
      ];

      if (
        !['lookup-nosecondary', 'lookup', 'array', 'object'].includes(paramDef.inputElement) &&
        !typesAllowed.includes(argType)
      ) {
        const expectedTypes = UIUtils.renderAndJoin(
          typesAllowed.map((type: string) => {
            const label = TYPE_LABELS[type];
            const article = ['a', 'e', 'i', 'o', 'u'].includes(label.charAt(0)) ? 'an' : 'a';

            return `${article} ${label}`;
          }),
          (v: any) => v,
          ', ',
          ' or ',
        ).join('');

        throw new Error(
          `Argument ${index + 1} of ${rule.label} should be ${expectedTypes}, but ${argTypeWithArticle} was provided.`,
        );
      }
    });
  }

  // conversion to ast
  const optionalParamNames = rule.optionalParams?.map(({ name }) => name);
  const ruleParamNames = [...(rule.requiredParams || []), ...(optionalParamNames || [])];

  return {
    type: 'function',
    name: rule.value,
    parameters: ruleParamNames.reduce(
      (acc: any, param: string, index: number) => {
        let argObj: any;

        if (isCustomTx && param === 'attrTxId') {
          const { customTxId } = rule;
          const transform = transforms?.[customTxId];
          const customTxParams = transform?.custom_config?.ui?.txArgs?.items;

          argObj = {
            name: 'attrTxId',
            type: 'number',
            value: customTxId,
            argValues: args.map((arg: any, index: number) => ({
              ...arg,
              fName: customTxParams?.[index]?.name || `arg-${index + 1}`,
            })),
          };
        }

        if (argObj) {
          acc[param] = argObj || args[index];
        }

        // this condition makes sure not to add optional params when they're not
        // specified
        else if (index < args.length) {
          acc[param] = args[index];
        }

        return acc;
      },
      rule.ignoreSrc
        ? {
            __srcAttr: {
              type: 'attributeRef',
              name: '',
              __ignoreSrc: true,
            },
          }
        : {},
    ),
  };
};

const parse = (
  statement: string,
  transforms: any = null,
  rulesByName: any = null,
): TransformDSLAST => {
  const { destAttr, funcCallStr } = extractDestAttr(statement);

  // Now lexify and parse just the function call part
  const lexResult = lexerInstance.tokenize(funcCallStr);

  if (lexResult.errors.length > 0) {
    throw new Error(lexResult.errors[0].message);
  }

  // Reuse the singleton parser instance
  parserInstance.input = lexResult.tokens;
  const cst = parserInstance.functionCall();

  if (parserInstance.errors.length > 0) {
    throw new Error(parserInstance.errors[0].message);
  }

  const result = visitorInstance.functionCall(cst, transforms, rulesByName);

  // Add the destination attribute if it exists
  if (destAttr) {
    result.__destAttr = destAttr.replace(/\\=/g, '=');
  }

  return result;
};

// Tokens
export const WhiteSpace = createToken({
  name: 'WhiteSpace',
  pattern: /[\s\n]+/,
  group: Lexer.SKIPPED,
});

export const Identifier = createToken({
  name: 'Identifier',
  pattern: /[a-zA-Z_$][$\w\s]*/,
  start_chars_hint: getCharList('a', 'z').concat(getCharList('A', 'Z'), '_', '$'),
});

export const FunctionName = createToken({
  name: 'FunctionName',
  pattern: /[a-zA-Z_][\w\s\n]*(?=\()/,
  start_chars_hint: getCharList('a', 'z').concat(getCharList('A', 'Z'), '_'),
});

export const StringLiteral = createToken({
  name: 'StringLiteral',
  pattern: /'(\\'|[^'])*'|"(\\"|[^"])*"/,
  start_chars_hint: ["'", '"'],
});

export const NumberLiteral = createToken({
  name: 'NumberLiteral',
  pattern: /-?\d*\.?\d+/,
  start_chars_hint: getCharList('0', '9').concat('-', '.'),
});

export const BooleanLiteral = createToken({
  name: 'BooleanLiteral',
  pattern: /true|false/,
  start_chars_hint: ['t', 'f'],
});

export const NullLiteral = createToken({
  name: 'NullLiteral',
  pattern: /null/,
  start_chars_hint: ['n'],
});

export const AttributeRef = createToken({
  name: 'AttributeRef',
  pattern: /\$\((\\[()]|[^)])+\)/,
  start_chars_hint: ['$'],
});

export const MetadataRef = createToken({
  name: 'MetadataRef',
  pattern: /\$\$\((\\[()]|[^)])+\)/,
  start_chars_hint: ['$'],
});

export const Comma = createToken({
  name: 'Comma',
  pattern: /,/,
  start_chars_hint: [','],
});

export const Equals = createToken({
  name: 'Equals',
  pattern: /=/,
  start_chars_hint: ['='],
});

export const LParen = createToken({
  name: 'LParen',
  pattern: /\(/,
  start_chars_hint: ['('],
});

export const RParen = createToken({
  name: 'RParen',
  pattern: /\)/,
  start_chars_hint: [')'],
});

export const LCurly = createToken({
  name: 'LCurly',
  pattern: /{/,
  start_chars_hint: ['{'],
});

export const RCurly = createToken({
  name: 'RCurly',
  pattern: /}/,
  start_chars_hint: ['}'],
});

export const LSquare = createToken({
  name: 'LSquare',
  pattern: /\[/,
  start_chars_hint: ['['],
});

export const RSquare = createToken({
  name: 'RSquare',
  pattern: /\]/,
  start_chars_hint: [']'],
});

export const Colon = createToken({
  name: 'Colon',
  pattern: /:/,
  start_chars_hint: [':'],
});

// All tokens in order of precedence
const allTokens = [
  WhiteSpace,
  StringLiteral,
  NumberLiteral,
  BooleanLiteral,
  NullLiteral,
  AttributeRef,
  MetadataRef,
  Comma,
  Equals,
  LParen,
  RParen,
  LCurly,
  RCurly,
  LSquare,
  RSquare,
  Colon,
  FunctionName,
  Identifier,
];

const lexerInstance = new Lexer(allTokens, {
  skipValidations: true,
});
const parserInstance = new TransformDSLParser();
const visitorInstance = new TransformDSLVisitor();

export default { parse };
